// src/component-files/forms/NewVendor.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    IconButton,
    Typography,
    CircularProgress,
    useTheme,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { tokens } from "../../theme";

const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_vendors";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Subcategories";
const API_PROGRAMS = "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";

export default function NewVendor({ onClose, onSaved }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const blankRow = {
        id: uuidv4(),
        VendorName: "",
        Address: "",
        City: "",
        State: "",
        ZipCode: "",
        BlanketPONumber: "",
        BudgetAmount: 0,
        CategoryID: "",
        CategoryName: "",
        SubCategoryID: "",
        SubCategoryName: "",
        ReimbursmentTrackingProgram: "",
        ReimbursementTrackingId: null,
        isActive: true,
    };

    const [rows, setRows] = useState([{ ...blankRow }]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [programs, setPrograms] = useState([]);

    useEffect(() => {
        Promise.all([
            axios.get(API_CATEGORIES),
            axios.get(API_SUBCATEGORIES),
            axios.get(API_PROGRAMS),
        ])
            .then(([cats, subs, progs]) => {
                setCategories(cats.data.filter(c => c.Type === "Expense"));
                setSubcategories(subs.data.filter(s => s.Type === "Expense"));
                setPrograms(progs.data);
            })
            .catch(err => {
                console.error(err);
                setError("Lookup load failed");
            })
            .finally(() => setLoading(false));
    }, []);

    const addRow = () =>
        setRows(r => [...r, { ...blankRow, id: uuidv4() }]);

    const removeRow = id =>
        setRows(r => r.filter(x => x.id !== id));

    const handleCellEdit = useCallback(params => {
        setRows(r =>
            r.map(row => {
                if (row.id !== params.id) return row;
                let updated = { ...row, [params.field]: params.value };

                // cascading names
                if (params.field === "CategoryID") {
                    const sel = categories.find(c => c.CategoryID === params.value);
                    updated.CategoryName = sel ? sel.CategoryName : "";
                    updated.SubCategoryID = "";
                    updated.SubCategoryName = "";
                }
                if (params.field === "SubCategoryID") {
                    const sel = subcategories.find(s => s.SubcategoryID === params.value);
                    updated.SubCategoryName = sel ? sel.SubcategoryName : "";
                }
                if (params.field === "ReimbursmentTrackingProgram") {
                    const sel = programs.find(p => p.program_abbreviation === params.value);
                    updated.ReimbursementTrackingId = sel ? sel.programid : null;
                }
                return updated;
            })
        );
    }, [categories, subcategories, programs]);

    const handleSaveAll = async () => {
        setError("");
        setSaving(true);
        try {
            for (let row of rows) {
                const payload = { ...row };
                delete payload.id;
                await axios.post(API_VENDORS, payload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            console.error(err);
            setError("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { field: "VendorName", headerName: "Vendor Name", flex: 1, editable: true },
        { field: "Address", headerName: "Address", flex: 1, editable: true },
        { field: "City", headerName: "City", flex: 0.7, editable: true },
        { field: "State", headerName: "State", flex: 0.5, editable: true },
        { field: "ZipCode", headerName: "Zip", flex: 0.5, editable: true },
        {
            field: "BlanketPONumber",
            headerName: "Blanket PO",
            flex: 1,
            editable: true,
        },
        {
            field: "BudgetAmount",
            headerName: "Budget Amount",
            flex: 1,
            editable: true,
            type: "number",
            valueFormatter: ({ value }) =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(value),
        },
        {
            field: "CategoryID",
            headerName: "Category",
            flex: 1,
            editable: true,
            renderCell: ({ row }) => row.CategoryName,
            renderEditCell: ({ id, value, api }) => (
                <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={value || ""}
                        label="Category"
                        onChange={e =>
                            api.setEditCellValue({ id, field: "CategoryID", value: e.target.value })
                        }
                        sx={{
                            backgroundColor: colors.primary[500],
                            color: theme.palette.text.primary,
                        }}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {categories.map(c => (
                            <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                {c.CategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ),
        },
        {
            field: "SubCategoryID",
            headerName: "Subcategory",
            flex: 1,
            editable: true,
            renderCell: ({ row }) => row.SubCategoryName,
            renderEditCell: ({ id, value, api, row }) => {
                const opts = subcategories.filter(
                    s => String(s.CategoryID) === String(row.CategoryID)
                );
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel>Subcategory</InputLabel>
                        <Select
                            value={value || ""}
                            label="Subcategory"
                            onChange={e =>
                                api.setEditCellValue({ id, field: "SubCategoryID", value: e.target.value })
                            }
                            sx={{
                                backgroundColor: colors.primary[500],
                                color: theme.palette.text.primary,
                            }}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {opts.map(s => (
                                <MenuItem key={s.SubcategoryID} value={s.SubcategoryID}>
                                    {s.SubcategoryName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            field: "ReimbursmentTrackingProgram",
            headerName: "Reimb. Program",
            flex: 1,
            editable: true,
            renderCell: ({ row }) => row.ReimbursmentTrackingProgram,
            renderEditCell: ({ id, value, api }) => (
                <FormControl fullWidth size="small">
                    <InputLabel>Program</InputLabel>
                    <Select
                        value={value || ""}
                        label="Program"
                        onChange={e =>
                            api.setEditCellValue({
                                id,
                                field: "ReimbursmentTrackingProgram",
                                value: e.target.value,
                            })
                        }
                        sx={{
                            backgroundColor: colors.primary[500],
                            color: theme.palette.text.primary,
                        }}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {programs.map(p => (
                            <MenuItem key={p.programid} value={p.program_abbreviation}>
                                {p.program_abbreviation}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ),
        },
        {
            field: "isActive",
            headerName: "Active",
            type: "boolean",
            flex: 0.5,
            editable: true,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "",
            width: 80,
            getActions: ({ id }) => [
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Remove"
                    onClick={() => removeRow(id)}
                    showInMenu={false}
                    key="del"
                />,
            ],
        },
    ];

    if (loading) {
        return (
            <Box p={2} textAlign="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                p: 2,
                borderRadius: 2,
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h6">New Vendor Entries</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {error && <Typography color="error">{error}</Typography>}

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                editMode="cell"
                onCellEditCommit={handleCellEdit}
                sx={{
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: colors.primary[600],
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: colors.primary[700],
                    },
                    "& .MuiDataGrid-cell:focus": {
                        outline: `2px solid ${theme.palette.secondary.main}`,
                    },
                }}
                autoHeight
                hideFooter
            />

            <Box mt={2} display="flex" gap={1}>
                <Button variant="outlined" onClick={addRow}>
                    Add Row
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveAll}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={20} /> : "Save All"}
                </Button>
            </Box>
        </Box>
    );
}
